import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ChatInterface.css';
import { api, Message, UserResponse } from '../utils/api';
import { loggingService } from '../services/loggingService';
import LogViewer from './LogViewer';
import ConversationSidebar, { Conversation } from './ConversationSidebar';
import Avatar from './Avatar';
import TypingIndicator from './TypingIndicator';
import EncryptionBanner from './EncryptionBanner';

interface ChatInterfaceProps {
  clientId: string;
  username?: string;
  onLogout: () => void;
  appName?: string;
  appTagline?: string;
}

/**
 * Helper function to format message time
 */
const formatMessageTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

/**
 * Helper function to format conversation time
 */
const formatConversationTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return formatMessageTime(timestamp);
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return date.toLocaleDateString([], { weekday: 'short' });
  } else {
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  }
};

/**
 * Helper function to get date separator text
 */
const getDateSeparator = (timestamp: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else {
    return date.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
  }
};

/**
 * Helper function to check if we need a date separator between two messages
 */
const needsDateSeparator = (currentMsg: Message, prevMsg: Message | null): boolean => {
  if (!prevMsg) return true;
  
  const currentDate = new Date(currentMsg.timestamp).toDateString();
  const prevDate = new Date(prevMsg.timestamp).toDateString();
  
  return currentDate !== prevDate;
};

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  clientId,
  username,
  onLogout,
  appName,
  appTagline,
}) => {
  const [currentUser, setCurrentUser] = useState<UserResponse | null>(null);
  const [receiverId, setReceiverId] = useState('');
  const [receiverUsername, setReceiverUsername] = useState('');
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentMessagesPlaintext, setSentMessagesPlaintext] = useState<Record<number, string>>({});
  const [decryptedMessages, setDecryptedMessages] = useState<Record<number, string>>({});
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  
  // Scroll management - prevents auto-scroll when user is reading history
  const [isUserNearBottom, setIsUserNearBottom] = useState(true);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const [shouldScrollOnNextUpdate, setShouldScrollOnNextUpdate] = useState(false);
  const previousMessagesCountRef = useRef(0);
  
  // Username management
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [updatingUsername, setUpdatingUsername] = useState(false);
  
  // User search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserResponse[]>([]);
  const [searching, setSearching] = useState(false);
  
  // Conversations list (for sidebar)
  const [conversations, setConversations] = useState<Conversation[]>([]);
  
  // Typing indicator (simulated for now, would need WebSocket for real implementation)
  const [isRecipientTyping, setIsRecipientTyping] = useState(false);
  
  // Emoji picker
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  
  // Common emojis for quick access
  const commonEmojis = [
    '😀', '😂', '😊', '🥰', '😍', '🤩', '😎', '🤔',
    '😅', '😇', '🙂', '😉', '😋', '😜', '🤗', '🤭',
    '👍', '👎', '👏', '🙌', '🤝', '✌️', '🤞', '💪',
    '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '💔',
    '🎉', '🎊', '🎁', '🎈', '✨', '🌟', '💫', '⭐',
    '🔥', '💯', '✅', '❌', '⚡', '💡', '🎯', '🚀',
  ];
  
  // Handle emoji selection
  const handleEmojiSelect = (emoji: string) => {
    setMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };
  
  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };
    
    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  // Handle scroll position to detect if user is near bottom
  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current) return;
    
    const container = messagesContainerRef.current;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // Consider "near bottom" if within 100px of the bottom
    const threshold = 100;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < threshold;
    
    setIsUserNearBottom(isNearBottom);
    
    // Clear new messages indicator when user scrolls to bottom
    if (isNearBottom) {
      setHasNewMessages(false);
    }
  }, []);
  
  // Attach scroll listener to messages container
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Load current user info on mount
  useEffect(() => {
    const loadCurrentUser = async () => {
      loggingService.info('CHAT_INTERFACE', 'Loading current user', { clientId });
      try {
        loggingService.trackApiCall('GET', '/users/me');
        const user = await api.getCurrentUser();
        loggingService.info('CHAT_INTERFACE', 'Current user loaded', {
          userId: user.userId,
          username: user.username,
          propsClientId: clientId,
        });
        
        if (clientId !== user.userId) {
          loggingService.warn('CHAT_INTERFACE', 'ID mismatch detected', {
            propsClientId: clientId,
            apiUserId: user.userId,
          });
        }
        
        loggingService.trackStateChange('currentUser', currentUser, user);
        setCurrentUser(user);
        
        // If user doesn't have a username set, show modal
        if (!user.username || user.username.startsWith('user_')) {
          setShowUsernameModal(true);
          setNewUsername(user.username || '');
        }
      } catch (err: any) {
        loggingService.error('CHAT_INTERFACE', 'Failed to load current user', {
          error: err?.message,
          stack: err?.stack,
        });
      }
    };
    loadCurrentUser();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);
  
  // Clear any error state on mount
  useEffect(() => {
    setError(null);
  }, [clientId]);
  
  // Search users when search query changes (debounced)
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    
    const timeoutId = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await api.searchUsers(searchQuery.trim());
        setSearchResults(results);
      } catch (err) {
        console.error('Failed to search users:', err);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalMessages, setTotalMessages] = useState(0);

  // Load messages function
  const loadMessages = useCallback(async (page: number = 1) => {
    loggingService.info('LOAD_MESSAGES', '===== LOADING MESSAGES =====', {
      receiverId,
      page,
      pageSize,
      clientId,
      currentUserId: currentUser?.userId,
    });

    if (!receiverId) {
      loggingService.warn('LOAD_MESSAGES', 'No receiverId, skipping');
      return;
    }

    if (!currentUser || !currentUser.userId) {
      loggingService.warn('LOAD_MESSAGES', 'currentUser not loaded yet, skipping load');
      return;
    }

    const authenticatedUserId = currentUser.userId;
    
    loggingService.debug('LOAD_MESSAGES', 'Query parameters', {
      clientId,
      authenticatedUserId,
      receiverId,
      page,
      pageSize,
    });

    try {
      loggingService.trackApiCall('GET', `/messages/conversation/${receiverId}`, { page, pageSize });
      const response = await api.getConversation(authenticatedUserId, receiverId, page, pageSize);
      
      loggingService.info('LOAD_MESSAGES', 'Response received', {
        messageCount: response.messages.length,
        page: response.page,
        pageSize: response.pageSize,
        totalCount: response.totalCount,
      });
      
      if (response.messages.length > 0) {
        loggingService.debug('LOAD_MESSAGES', 'First message', {
          id: response.messages[0].id,
          senderId: response.messages[0].senderId,
          receiverId: response.messages[0].receiverId,
          timestamp: response.messages[0].timestamp,
        });
        loggingService.debug('LOAD_MESSAGES', 'Last message', {
          id: response.messages[response.messages.length - 1].id,
          senderId: response.messages[response.messages.length - 1].senderId,
          receiverId: response.messages[response.messages.length - 1].receiverId,
          timestamp: response.messages[response.messages.length - 1].timestamp,
        });
      } else {
        loggingService.warn('LOAD_MESSAGES', 'No messages returned', {
          authenticatedUserId,
          receiverId,
          page,
        });
      }
      
      loggingService.trackStateChange('messages', messages, response.messages);
      loggingService.trackStateChange('totalMessages', totalMessages, response.totalCount);
      loggingService.trackStateChange('currentPage', currentPage, response.page);
      
      setMessages(response.messages);
      setTotalMessages(response.totalCount);
      setCurrentPage(response.page);
      
      loggingService.info('LOAD_MESSAGES', 'State updated', {
        messagesCount: response.messages.length,
        totalCount: response.totalCount,
        page: response.page,
      });
      
      loggingService.info('LOAD_MESSAGES', '===== MESSAGES LOADED =====');
    } catch (err: any) {
      loggingService.error('LOAD_MESSAGES', 'Load messages error', {
        error: err?.message,
        stack: err?.stack,
        receiverId,
        authenticatedUserId,
      });
    }
  }, [receiverId, clientId, currentUser, pageSize, messages, totalMessages, currentPage]);

  useEffect(() => {
    if (receiverId && currentUser && currentUser.userId) {
      loggingService.debug('POLLING', 'Starting message polling', { receiverId, currentUserId: currentUser.userId });
      loadMessages(1);
      const interval = setInterval(() => {
        loggingService.debug('POLLING', 'Polling for messages', { receiverId });
        loadMessages(1);
      }, 15000);
      return () => {
        loggingService.debug('POLLING', 'Stopping message polling', { receiverId });
        clearInterval(interval);
      };
    } else {
      loggingService.debug('POLLING', 'Not starting polling', { receiverId, hasCurrentUser: !!currentUser });
    }
  }, [receiverId, currentUser, loadMessages]);

  // Smart scroll behavior - only auto-scroll when appropriate
  useEffect(() => {
    const currentCount = messages.length;
    const previousCount = previousMessagesCountRef.current;
    const isNewConversation = previousCount === 0 && currentCount > 0;
    const hasNewMessagesArrived = currentCount > previousCount;
    
    loggingService.debug('SCROLL', 'Evaluating scroll behavior', {
      currentCount,
      previousCount,
      isUserNearBottom,
      isNewConversation,
      hasNewMessagesArrived,
      shouldScrollOnNextUpdate,
    });
    
    // Always scroll for new conversation or when explicitly requested (after sending)
    if (isNewConversation || shouldScrollOnNextUpdate) {
      scrollToBottom();
      setShouldScrollOnNextUpdate(false);
    } 
    // Only scroll for new messages if user is near bottom
    else if (hasNewMessagesArrived && isUserNearBottom) {
      scrollToBottom();
    }
    // Show "new messages" indicator if user has scrolled up and new messages arrived
    else if (hasNewMessagesArrived && !isUserNearBottom) {
      setHasNewMessages(true);
    }
    
    // Update previous count
    previousMessagesCountRef.current = currentCount;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  // Helper function to ensure keys exist for a client ID
  const ensureKeysExist = async (clientId: string): Promise<boolean> => {
    loggingService.info('ENSURE_KEYS', '===== ENSURE_KEYS CALLED =====', { 
      clientId,
      timestamp: new Date().toISOString(),
    });
    
    try {
      loggingService.info('ENSURE_KEYS', 'Step 1: Checking if public key exists', { clientId });
      loggingService.trackApiCall('GET', `/keys/${clientId}`, { clientId });
      
      const publicKey = await api.getPublicKey(clientId);
      loggingService.info('ENSURE_KEYS', 'Keys already exist', { 
        clientId,
        publicKeyLength: publicKey?.length || 0,
      });
      return true;
    } catch (err: any) {
      const isNotFound = err?.message?.includes('not found') || 
                        err?.message?.includes('Public key not found') ||
                        err?.message?.includes('404') ||
                        err?.message?.includes('NotFound');
      
      loggingService.debug('ENSURE_KEYS', 'getPublicKey failed', {
        clientId,
        error: err?.message,
        isNotFound,
        status: err?.response?.status,
      });
      
      if (!isNotFound) {
        loggingService.warn('ENSURE_KEYS', 'Cannot check keys (not a 404 error)', {
          clientId,
          error: err?.message,
          status: err?.status || err?.response?.status,
          statusText: err?.response?.statusText,
          isNotFound,
        });
        loggingService.error('ENSURE_KEYS', '===== ENSURE_KEYS FAILED (NON-404) =====', {
          clientId,
          error: err?.message,
          status: err?.status || err?.response?.status,
        });
        return false;
      }
      
      loggingService.info('ENSURE_KEYS', 'Step 2: Public key not found, attempting to generate keys', { 
        clientId,
        errorFromGetPublicKey: err?.message,
        statusFromGetPublicKey: err?.status || err?.response?.status,
      });
      
      try {
        loggingService.info('ENSURE_KEYS', 'Calling api.generateKeyPair', { clientId });
        loggingService.trackApiCall('POST', '/keys/generate', { clientId });
        
        const result = await api.generateKeyPair(clientId);
        
        loggingService.info('ENSURE_KEYS', 'Keys generated successfully', {
          clientId,
          publicKeyLength: result.publicKey?.length || 0,
          hasPublicKey: !!result.publicKey,
        });
        loggingService.info('ENSURE_KEYS', '===== ENSURE_KEYS SUCCESS =====', { clientId });
        return true;
      } catch (genErr: any) {
        let errorDetails: any = {
          clientId,
          error: genErr?.message,
          stack: genErr?.stack,
          status: genErr?.response?.status,
          statusText: genErr?.response?.statusText,
        };
        
        if (genErr?.response) {
          try {
            const clonedResponse = genErr.response.clone();
            const errorData = await clonedResponse.json();
            errorDetails.errorData = errorData;
            errorDetails.errorMessage = errorData.error || genErr?.message;
            
            if (errorData.errors) {
              const validationErrors = Object.entries(errorData.errors)
                .map(([key, value]: [string, any]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
                .join('; ');
              errorDetails.validationErrors = validationErrors;
            }
            
            loggingService.error('ENSURE_KEYS', 'Error response details', errorDetails);
          } catch (parseErr) {
            errorDetails.parseError = parseErr;
            if (genErr?.fullErrorText) {
              errorDetails.fullErrorText = genErr.fullErrorText;
            }
            loggingService.error('ENSURE_KEYS', 'Could not parse error response', errorDetails);
          }
        } else {
          if (genErr?.fullErrorText) {
            errorDetails.fullErrorText = genErr.fullErrorText;
          }
          loggingService.error('ENSURE_KEYS', 'Failed to generate keys (no response)', errorDetails);
        }
        
        if (genErr?.fullErrorText) {
          errorDetails.fullErrorText = genErr.fullErrorText;
        }
        
        console.error('[ensureKeysExist] Full error details:', {
          clientId,
          error: genErr,
          message: genErr?.message,
          status: genErr?.status,
          response: genErr?.response,
        });
        
        loggingService.error('ENSURE_KEYS', '===== ENSURE_KEYS FAILED =====', {
          clientId,
          finalError: errorDetails,
        });
        
        return false;
      }
    }
  };

  useEffect(() => {
    loggingService.info('DECRYPT_ALL', '===== PROCESSING MESSAGES =====', {
      messagesCount: messages.length,
      clientId,
      authenticatedUserId: currentUser?.userId,
      sentMessagesPlaintextCount: Object.keys(sentMessagesPlaintext).length,
    });

    if (messages.length === 0) {
      loggingService.debug('DECRYPT_ALL', 'No messages, clearing decrypted messages');
      setDecryptedMessages({});
      return;
    }

    const authenticatedUserId = currentUser?.userId || clientId;
    if (!authenticatedUserId) {
      loggingService.warn('DECRYPT_ALL', 'No authenticated user ID, skipping');
      return;
    }
    
    const currentMessageIds = messages.map(m => m.id).sort().join(',');
    let isCancelled = false;
    const currentMessages = [...messages];

    const processMessages = async () => {
      const decrypted: Record<number, string> = {};
      
      let serverDecryptedCount = 0;
      let needsClientDecryption: typeof currentMessages = [];
      
      for (const msg of currentMessages) {
        if (isCancelled) return;
        
        if (sentMessagesPlaintext[msg.id]) {
          decrypted[msg.id] = sentMessagesPlaintext[msg.id];
          continue;
        }
        
        if (msg.content !== undefined && msg.content !== null) {
          decrypted[msg.id] = msg.content;
          serverDecryptedCount++;
          continue;
        }
        
        if (msg.decryptionError) {
          decrypted[msg.id] = `[Server decryption error: ${msg.decryptionError}]`;
          continue;
        }
        
        const isSent = msg.senderId === authenticatedUserId;
        const isReceived = msg.receiverId === authenticatedUserId;
        
        if (isSent || isReceived) {
          needsClientDecryption.push(msg);
        } else {
          decrypted[msg.id] = '[Encrypted message - not for this user]';
        }
      }
      
      loggingService.info('DECRYPT_ALL', 'Message processing summary', {
        total: currentMessages.length,
        serverDecrypted: serverDecryptedCount,
        needsClientDecryption: needsClientDecryption.length,
        fromPlaintext: Object.keys(sentMessagesPlaintext).length,
      });
      
      if (needsClientDecryption.length === 0) {
        if (!isCancelled) {
          const latestMessageIds = messages.map(m => m.id).sort().join(',');
          if (latestMessageIds === currentMessageIds) {
            setDecryptedMessages(prev => ({ ...prev, ...decrypted }));
          }
        }
        loggingService.info('DECRYPT_ALL', '===== ALL MESSAGES READY (NO CLIENT DECRYPT) =====');
        return;
      }
      
      loggingService.warn('DECRYPT_ALL', 'Falling back to client-side decryption', {
        count: needsClientDecryption.length,
      });
      
      const uniqueUserIds = new Set<string>();
      uniqueUserIds.add(authenticatedUserId);
      for (const msg of needsClientDecryption) {
        uniqueUserIds.add(msg.senderId);
        uniqueUserIds.add(msg.receiverId);
      }
      
      const keyChecks = Array.from(uniqueUserIds).map(userId => 
        ensureKeysExist(userId).then(hasKeys => ({ userId, hasKeys }))
      );
      const keyCheckResults = await Promise.all(keyChecks);
      const keysMap = new Map<string, boolean>();
      for (const result of keyCheckResults) {
        keysMap.set(result.userId, result.hasKeys);
      }

      const decryptionPromises = needsClientDecryption.map(async (msg) => {
        if (isCancelled) return { id: msg.id, text: '[Cancelled]' };
        
        const isSent = msg.senderId === authenticatedUserId;
        const senderIdForDecrypt = isSent ? msg.receiverId : msg.senderId;
        
        try {
          const senderHasKeys = keysMap.get(msg.senderId) ?? false;
          const receiverHasKeys = keysMap.get(msg.receiverId) ?? false;
          
          if (!senderHasKeys || !receiverHasKeys) {
            return { id: msg.id, text: '[Keys not available]' };
          }
          
          const decryptedText = await api.decryptMessage(msg.encryptedContent, senderIdForDecrypt);
          return { id: msg.id, text: decryptedText };
        } catch (err: any) {
          return { id: msg.id, text: `[Decryption error: ${err?.message || 'Failed'}]` };
        }
      });

      const decryptionResults = await Promise.all(decryptionPromises);
      
      if (isCancelled) return;
      
      for (const result of decryptionResults) {
        decrypted[result.id] = result.text;
      }
      
      const latestMessageIds = messages.map(m => m.id).sort().join(',');
      if (currentMessageIds === latestMessageIds) {
        setDecryptedMessages(prev => ({ ...prev, ...decrypted }));
      }
      
      loggingService.info('DECRYPT_ALL', '===== PROCESSING COMPLETE =====');
    };

    processMessages();
    
    return () => {
      const latestMessageIds = messages.map(m => m.id).sort().join(',');
      if (currentMessageIds !== latestMessageIds) {
        isCancelled = true;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, clientId, currentUser?.userId, sentMessagesPlaintext]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setHasNewMessages(false);
    setIsUserNearBottom(true);
  }, []);
  
  // Handle click on "new messages" button to scroll to bottom
  const handleScrollToNewMessages = () => {
    scrollToBottom();
  };

  const handleUpdateUsername = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUsername.trim() || newUsername.trim().length < 3) {
      setError('Username must be at least 3 characters');
      return;
    }
    
    setUpdatingUsername(true);
    setError(null);
    
    try {
      const updated = await api.updateUsername(newUsername.trim());
      setCurrentUser(updated);
      setShowUsernameModal(false);
    } catch (err: any) {
      setError(err.message || 'Failed to update username');
    } finally {
      setUpdatingUsername(false);
    }
  };
  
  const handleSelectUser = async (user: UserResponse) => {
    setReceiverId(user.userId);
    setReceiverUsername(user.username);
    setSearchQuery('');
    setSearchResults([]);
    
    // Add to conversations if not already there
    setConversations(prev => {
      const exists = prev.find(c => c.userId === user.userId);
      if (exists) return prev;
      return [{ userId: user.userId, username: user.username, isOnline: true }, ...prev];
    });
  };
  
  const handleSearchByUsername = async () => {
    if (!searchQuery.trim()) {
      setError('Please enter a username to search');
      return;
    }
    
    setSearching(true);
    setError(null);
    
    try {
      const user = await api.getUserByUsername(searchQuery.trim());
      setReceiverId(user.userId);
      setReceiverUsername(user.username);
      setSearchQuery('');
      setSearchResults([]);
      
      // Add to conversations
      setConversations(prev => {
        const exists = prev.find(c => c.userId === user.userId);
        if (exists) return prev;
        return [{ userId: user.userId, username: user.username, isOnline: true }, ...prev];
      });
    } catch (err: any) {
      const errorMessage = err?.message || 'User not found';
      setError(errorMessage);
      console.error('Failed to get user:', err);
    } finally {
      setSearching(false);
    }
  };
  
  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    loggingService.info('SEND_MESSAGE', '===== STARTING MESSAGE SEND =====', {
      messageLength: message.trim().length,
      receiverId,
      hasCurrentUser: !!currentUser,
      currentUserId: currentUser?.userId,
    });
    
    if (!message.trim() || !receiverId.trim()) {
      loggingService.warn('SEND_MESSAGE', 'Message or receiverId is empty');
      return;
    }

    if (!currentUser || !currentUser.userId) {
      loggingService.error('SEND_MESSAGE', 'currentUser not loaded yet!');
      setError('User information not loaded. Please wait a moment and try again.');
      return;
    }

    const plaintextMessage = message.trim();
    const authenticatedUserId = currentUser.userId;
    
    loggingService.debug('SEND_MESSAGE', 'Message send parameters', {
      plaintextLength: plaintextMessage.length,
      clientId,
      authenticatedUserId,
      receiverId,
    });
    
    if (clientId !== authenticatedUserId) {
      loggingService.warn('SEND_MESSAGE', 'ID mismatch detected', {
        propsClientId: clientId,
        authenticatedUserId,
      });
    }
    
    setLoading(true);
    setError(null);

    try {
      loggingService.debug('SEND_MESSAGE', 'Step 1: Checking keys exist', {
        authenticatedUserId,
        receiverId,
      });
      
      const senderHasKeys = await ensureKeysExist(authenticatedUserId);
      const receiverHasKeys = await ensureKeysExist(receiverId);
      
      loggingService.debug('SEND_MESSAGE', 'Key check results', {
        senderHasKeys,
        receiverHasKeys,
      });
      
      if (!senderHasKeys) {
        const recentErrors = loggingService.getLogsByCategory('ENSURE_KEYS')
          .filter(log => log.level === 'ERROR' && log.data?.clientId === authenticatedUserId)
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        
        let errorMessage = `Failed to generate encryption keys for your account (${authenticatedUserId}).\n\n`;
        
        if (recentErrors.length > 0) {
          const latestError = recentErrors[0];
          const errorData = latestError.data;
          
          if (errorData?.fullErrorText) {
            errorMessage += `Error Details:\n${errorData.fullErrorText}`;
          } else if (errorData?.validationErrors) {
            errorMessage += `Validation Error:\n${errorData.validationErrors}\n\n`;
            errorMessage += `Status: ${errorData.status || 'Unknown'}\n`;
            errorMessage += `Error: ${errorData.error || 'Unknown error'}`;
          } else if (errorData?.errorData) {
            const errData = errorData.errorData;
            if (errData.errors) {
              const validationErrors = Object.entries(errData.errors)
                .map(([key, value]: [string, any]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
                .join('\n');
              errorMessage += `Validation Errors:\n${validationErrors}\n\n`;
            }
            errorMessage += `Status: ${errorData.status || errData.status || 'Unknown'}\n`;
            errorMessage += `Error: ${errorData.error || errData.error || errData.title || 'Unknown error'}`;
            if (errData.traceId) {
              errorMessage += `\nTrace ID: ${errData.traceId}`;
            }
          } else {
            errorMessage += `Status: ${errorData?.status || 'Unknown'}\n`;
            errorMessage += `Error: ${errorData?.error || errorData?.errorMessage || 'Unknown error'}`;
          }
        } else {
          errorMessage += `No detailed error information available. Please check the logs (click "View Logs" button) for more details.`;
        }
        
        loggingService.error('SEND_MESSAGE', 'Sender keys not available', {
          authenticatedUserId,
          errorMessage,
        });
        
        setError(errorMessage);
        return;
      }
      if (!receiverHasKeys) {
        const recentErrors = loggingService.getLogsByCategory('ENSURE_KEYS')
          .filter(log => log.level === 'ERROR' && log.data?.clientId === receiverId)
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        
        let errorMessage = `Failed to generate encryption keys for the receiver (${receiverId}).\n\n`;
        
        if (recentErrors.length > 0) {
          const latestError = recentErrors[0];
          const errorData = latestError.data;
          
          if (errorData?.fullErrorText) {
            errorMessage += `Error Details:\n${errorData.fullErrorText}`;
          } else if (errorData?.validationErrors) {
            errorMessage += `Validation Error:\n${errorData.validationErrors}\n\n`;
            errorMessage += `Status: ${errorData.status || 'Unknown'}\n`;
            errorMessage += `Error: ${errorData.error || 'Unknown error'}`;
          } else {
            errorMessage += `Status: ${errorData?.status || 'Unknown'}\n`;
            errorMessage += `Error: ${errorData?.error || errorData?.errorMessage || 'Unknown error'}`;
          }
          
          errorMessage += `\n\nThe other user may need to log in first or there may be a connection issue.`;
        } else {
          errorMessage += `The other user may need to log in first or there may be a connection issue.\n\nPlease check the logs (click "View Logs" button) for more details.`;
        }
        
        loggingService.error('SEND_MESSAGE', 'Receiver keys not available', {
          receiverId,
          errorMessage,
        });
        
        setError(errorMessage);
        return;
      }

      loggingService.debug('SEND_MESSAGE', 'Step 2: Encrypting message', {
        messageLength: plaintextMessage.length,
        receiverId,
      });
      
      let encrypted: string;
      let mac: string;
      try {
        loggingService.trackApiCall('POST', '/messages/encrypt', { receiverId, messageLength: plaintextMessage.length });
        const encryptResult = await api.encryptMessage(plaintextMessage, receiverId);
        encrypted = encryptResult.encrypted;
        mac = encryptResult.mac;
        loggingService.info('SEND_MESSAGE', 'Encryption successful', {
          encryptedLength: encrypted.length,
          macLength: mac.length,
        });
      } catch (encryptErr: any) {
        loggingService.error('SEND_MESSAGE', 'Encryption failed', {
          error: encryptErr.message,
          stack: encryptErr.stack,
        });
        throw new Error(`Encryption failed: ${encryptErr.message || 'Unknown error'}`);
      }

      loggingService.debug('SEND_MESSAGE', 'Step 3: Sending message to backend', {
        senderId: authenticatedUserId,
        receiverId,
        encryptedLength: encrypted.length,
        macLength: mac.length,
      });
      
      let sentMessage: Message;
      try {
        loggingService.trackApiCall('POST', '/messages', {
          senderId: authenticatedUserId,
          receiverId,
          encryptedLength: encrypted.length,
          macLength: mac.length,
        });
        sentMessage = await api.sendMessage({
          senderId: authenticatedUserId,
          receiverId: receiverId,
          encryptedContent: encrypted,
          mac: mac,
        });
        loggingService.info('SEND_MESSAGE', 'Message sent successfully', {
          messageId: sentMessage.id,
          senderId: sentMessage.senderId,
          receiverId: sentMessage.receiverId,
          timestamp: sentMessage.timestamp,
        });
      } catch (sendErr: any) {
        loggingService.error('SEND_MESSAGE', 'Send message failed', {
          error: sendErr.message,
          stack: sendErr.stack,
        });
        throw new Error(`Failed to send message: ${sendErr.message || 'Unknown error'}`);
      }

      loggingService.debug('SEND_MESSAGE', 'Step 4: Storing plaintext and reloading messages', {
        messageId: sentMessage.id,
      });
      
      setSentMessagesPlaintext(prev => ({
        ...prev,
        [sentMessage.id]: plaintextMessage
      }));

      setMessage('');

      loggingService.debug('SEND_MESSAGE', 'Step 5: Reloading messages', { page: 1 });
      // Ensure we scroll to bottom after sending a message
      setShouldScrollOnNextUpdate(true);
      await loadMessages(1);
      
      // Update conversation preview
      setConversations(prev => {
        const updated = prev.map(c => {
          if (c.userId === receiverId) {
            return { ...c, lastMessage: plaintextMessage, lastMessageTime: formatConversationTime(sentMessage.timestamp) };
          }
          return c;
        });
        return updated;
      });
      
      loggingService.info('SEND_MESSAGE', '===== MESSAGE SEND COMPLETE =====');
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to send message';
      loggingService.error('SEND_MESSAGE', '===== MESSAGE SEND FAILED =====', {
        error: errorMessage,
        stack: err.stack,
      });
      setError(errorMessage);
    } finally {
      setLoading(false);
      loggingService.debug('SEND_MESSAGE', 'Loading state set to false');
    }
  };

  const handleClearConversation = () => {
    setReceiverId('');
    setReceiverUsername('');
    setMessages([]);
    setDecryptedMessages({});
  };

  const resolvedAppName = appName || 'Encrypted Chat (QeM SDK)';
  const resolvedAppTagline =
    (appTagline && appTagline.trim().length > 0 && appTagline.trim()) ||
    'Quantum-secure messaging node';

  return (
    <>
      <LogViewer />
      <div className="chat-app">
        {/* Conversation Sidebar */}
        <ConversationSidebar
          currentUser={currentUser}
          appName={resolvedAppName}
          appTagline={resolvedAppTagline}
          conversations={conversations}
          activeConversationId={receiverId}
          searchQuery={searchQuery}
          searchResults={searchResults}
          isSearching={searching}
          onSearchChange={setSearchQuery}
          onSearchSubmit={handleSearchByUsername}
          onSelectConversation={(userId, username) => {
            setReceiverId(userId);
            setReceiverUsername(username);
          }}
          onSelectSearchResult={handleSelectUser}
          onEditUsername={() => {
            setNewUsername(currentUser?.username || '');
            setShowUsernameModal(true);
          }}
          onLogout={onLogout}
        />

        {/* Main Chat Area */}
        <div className="chat-main">
          {receiverId ? (
            <>
              {/* Chat Header */}
              <div className="chat-header">
                <div className="chat-header__recipient">
                  <Avatar 
                    name={receiverUsername || receiverId} 
                    size="medium"
                    isOnline={true}
                    showOnlineStatus={true}
                  />
                  <div className="chat-header__info">
                    <h3 className="chat-header__name">{receiverUsername || receiverId}</h3>
                    <span className="chat-header__status">
                      <span className="chat-header__status-dot" />
                      Online
                    </span>
                  </div>
                </div>
                <div className="chat-header__actions">
                  <button 
                    className="chat-header__action-btn chat-header__action-btn--danger" 
                    title="Close conversation"
                    onClick={handleClearConversation}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Encryption Banner */}
              <EncryptionBanner recipientName={receiverUsername || receiverId.substring(0, 8)} />

              {/* Error Message */}
              {error && (
                <div className="chat-error">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>
                  <span>{error}</span>
                  <button onClick={() => setError(null)} className="chat-error__close">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
              )}

              {/* Messages Area */}
              <div className="messages-area" ref={messagesContainerRef}>
                {/* Pagination */}
                {totalMessages > pageSize && (
                  <div className="messages-pagination">
                    <button
                      onClick={() => {
                        const newPage = Math.max(1, currentPage - 1);
                        setCurrentPage(newPage);
                        loadMessages(newPage);
                      }}
                      disabled={currentPage === 1}
                      className="messages-pagination__btn"
                    >
                      Previous
                    </button>
                    <span className="messages-pagination__info">
                      Page {currentPage} of {Math.ceil(totalMessages / pageSize)}
                    </span>
                    <button
                      onClick={() => {
                        const newPage = currentPage + 1;
                        setCurrentPage(newPage);
                        loadMessages(newPage);
                      }}
                      disabled={currentPage >= Math.ceil(totalMessages / pageSize)}
                      className="messages-pagination__btn"
                    >
                      Next
                    </button>
                  </div>
                )}

                {messages.length === 0 ? (
                  <div className="messages-empty">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                    <p>No messages yet</p>
                    <span>Send a message to start the conversation</span>
                  </div>
                ) : (
                  <>
                    {messages.map((msg, index) => {
                      const authenticatedUserId = currentUser?.userId || clientId;
                      const isSent = msg.senderId === authenticatedUserId;
                      const decryptedText = decryptedMessages[msg.id];
                      const prevMsg = index > 0 ? messages[index - 1] : null;
                      const showDateSeparator = needsDateSeparator(msg, prevMsg);
                      
                      return (
                        <React.Fragment key={msg.id}>
                          {showDateSeparator && (
                            <div className="date-separator">
                              <span>{getDateSeparator(msg.timestamp)}</span>
                            </div>
                          )}
                          <div className={`message ${isSent ? 'message--sent' : 'message--received'}`}>
                            <div className="message__bubble">
                              <p className="message__text">
                                {decryptedText || (decryptedMessages[msg.id] === undefined ? 'Decrypting...' : '[Decryption failed]')}
                              </p>
                            </div>
                            <div className="message__meta">
                              <span className="message__encryption">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M12 1C9.24 1 7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2h-1V6c0-2.76-2.24-5-5-5zm0 2c1.66 0 3 1.34 3 3v2H9V6c0-1.66 1.34-3 3-3z"/>
                                </svg>
                              </span>
                              <span className="message__time">{formatMessageTime(msg.timestamp)}</span>
                              {isSent && (
                                <span className="message__status">
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"/>
                                  </svg>
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '-8px' }}>
                                    <polyline points="20 6 9 17 4 12"/>
                                  </svg>
                                </span>
                              )}
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })}
                    
                    {/* Typing Indicator */}
                    {isRecipientTyping && (
                      <TypingIndicator username={receiverUsername || receiverId.substring(0, 8)} />
                    )}
                  </>
                )}
                <div ref={messagesEndRef} />
                
                {/* New Messages Button - shows when user has scrolled up and new messages arrive */}
                {hasNewMessages && (
                  <button 
                    className="new-messages-btn"
                    onClick={handleScrollToNewMessages}
                    type="button"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                    New messages
                  </button>
                )}
              </div>

              {/* Message Input */}
              <form onSubmit={handleSendMessage} className="message-input-form">
                <button type="button" className="message-input__attachment" title="Attach file">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                  </svg>
                </button>
                <input
                  type="text"
                  placeholder="Type a secure message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="message-input__field"
                  disabled={loading}
                />
                <div className="message-input__emoji-wrapper" ref={emojiPickerRef}>
                  <button 
                    type="button" 
                    className={`message-input__emoji ${showEmojiPicker ? 'message-input__emoji--active' : ''}`}
                    title="Add emoji"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
                      <line x1="9" y1="9" x2="9.01" y2="9"/>
                      <line x1="15" y1="9" x2="15.01" y2="9"/>
                    </svg>
                  </button>
                  {showEmojiPicker && (
                    <div className="emoji-picker">
                      <div className="emoji-picker__header">
                        <span>Pick an emoji</span>
                        <button 
                          type="button"
                          className="emoji-picker__close"
                          onClick={() => setShowEmojiPicker(false)}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                          </svg>
                        </button>
                      </div>
                      <div className="emoji-picker__grid">
                        {commonEmojis.map((emoji, index) => (
                          <button
                            key={index}
                            type="button"
                            className="emoji-picker__emoji"
                            onClick={() => handleEmojiSelect(emoji)}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <button
                  type="submit"
                  className="message-input__send"
                  disabled={loading || !message.trim()}
                  title="Send encrypted message"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13"/>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                  </svg>
                  <span className="message-input__lock">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 1C9.24 1 7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2h-1V6c0-2.76-2.24-5-5-5zm0 2c1.66 0 3 1.34 3 3v2H9V6c0-1.66 1.34-3 3-3z"/>
                    </svg>
                  </span>
                </button>
              </form>
            </>
          ) : (
            /* Empty State - No conversation selected */
            <div className="chat-empty">
              <div className="chat-empty__content">
                <div className="chat-empty__icon">
                  <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    <path d="M12 8v4"/>
                    <path d="M12 16h.01"/>
                  </svg>
                </div>
                <h2>Welcome to {resolvedAppName}</h2>
                <p>Search for a user in the sidebar to start a secure, encrypted conversation.</p>
                <div className="chat-empty__features">
                  <div className="chat-empty__feature">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 1C9.24 1 7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2h-1V6c0-2.76-2.24-5-5-5zm0 2c1.66 0 3 1.34 3 3v2H9V6c0-1.66 1.34-3 3-3z"/>
                    </svg>
                    <span>End-to-end encrypted</span>
                  </div>
                  <div className="chat-empty__feature">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    <span>Secured by Sentry-Q</span>
                  </div>
                  <div className="chat-empty__feature">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z"/>
                    </svg>
                    <span>Secure messaging</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Username Modal */}
        {showUsernameModal && (
          <div className="modal-overlay" onClick={() => setShowUsernameModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal__header">
                <h3>Set Your Username</h3>
                <button 
                  className="modal__close" 
                  onClick={() => setShowUsernameModal(false)}
                  aria-label="Close modal"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>
              <p className="modal__description">
                Choose a username that others can use to find and chat with you.
              </p>
              <form onSubmit={handleUpdateUsername}>
                <input
                  type="text"
                  placeholder="Enter username (3-50 characters)"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  className="modal__input"
                  minLength={3}
                  maxLength={50}
                  autoFocus
                />
                {error && <div className="modal__error">{error}</div>}
                <div className="modal__actions">
                  <button
                    type="button"
                    className="modal__btn modal__btn--secondary"
                    onClick={() => setShowUsernameModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="modal__btn modal__btn--primary"
                    disabled={updatingUsername || !newUsername.trim() || newUsername.trim().length < 3}
                  >
                    {updatingUsername ? 'Saving...' : 'Save Username'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ChatInterface;
